/**
 * SiteGlue internal AI team — Jim Wanek + AI employees working alongside humans.
 * Used by /about/my-team and marketing nav.
 */

export type SiteGlueAiTeamMember = {
  id: string;
  name: string;
  title: string;
  avatarSrc: string;
  accentColor: string;
  email?: string;
  phoneDisplay?: string;
  externalHref?: string;
  externalLabel?: string;
  description: string;
  responsibilities: string[];
  /** Optional CTA action key for siteglue-cta helpers */
  cta?: "trish" | "bookDemo" | "contactSales" | "askVictoria";
};

export const SITEGLUE_AI_TEAM = [
  {
    id: "trish",
    name: "Trish",
    title: "AI Marketing Director",
    avatarSrc: "/marketing/trish-onboarding-agent.png",
    accentColor: "#9cbb5a",
    email: "trish@siteglue.ai",
    phoneDisplay: "(720) 443-2710",
    description:
      "Trish is SiteGlue’s AI Marketing Director. She learns about your organization, goals, channels, and scheduling needs—then builds a personalized proposal with one recommended SiteGlue Agent, clear outcomes, and a path to get started. Always a Human-in-the-Loop™: she can hand you to Jim for demos, custom development, or onboarding help.",
    responsibilities: [
      "Lead intake and discovery for new organizations",
      "Recommend AI Employees, channels, and integrations",
      "Prepare AI Employee Proposals with outcome-driven plans",
      "Guide career path and plan selection",
      "Hand off to Jim Wanek for demos and custom work",
    ],
    cta: "trish",
  },
  {
    id: "victoria",
    name: "Victoria",
    title: "Online Support Specialist",
    avatarSrc: "/images/profiles/Victoria.png",
    accentColor: "#4f81bd",
    email: "support@siteglue.ai",
    description:
      "Victoria is the SiteGlue AI on our website—ready when your team is away from the desk. She answers product questions, helps visitors book demos, routes sales inquiries, and keeps conversations moving with context your team can pick up through Team Handoff™ and Team Takeover™.",
    responsibilities: [
      "Answer product and pricing questions on siteglue.ai",
      "Book demos and capture sales intent",
      "Route Contact Sales requests with conversation context",
      "Support visitors after hours with clear next steps",
      "Escalate to humans when judgment or relationships matter",
    ],
    cta: "askVictoria",
  },
  {
    id: "helena",
    name: "Helena",
    title: "Marketing Director",
    avatarSrc: "/images/profiles/Samantha.webp",
    accentColor: "#9cbb5a",
    email: "marketing@siteglue.ai",
    externalHref: "https://www.enrichlabs.ai/ai-digital-marketing-agent",
    externalLabel: "Helena on EnrichLabs",
    description:
      "Helena leads SiteGlue marketing alongside Jim—turning product updates into campaigns, landing pages, and content that explain AI Employees in human terms. Built on EnrichLabs’ autonomous marketing agent, she keeps messaging aligned with Always a Human-in-the-Loop™ while accelerating SEO, copy, and go-to-market work.",
    responsibilities: [
      "Own SiteGlue brand voice and campaign messaging",
      "Draft and refine landing pages, blog posts, and email copy",
      "Coordinate SEO and content updates across siteglue.ai",
      "Translate product releases into customer-facing stories",
      "Open GitHub PRs for approved marketing changes",
    ],
  },
  {
    id: "clyde",
    name: "Clyde",
    title: "AI Coding Agent",
    avatarSrc: "/images/profiles/Ben.webp",
    accentColor: "#4f81bd",
    email: "engineering@siteglue.ai",
    description:
      "Clyde is SiteGlue’s AI developer—shipping features, fixing bugs, and extending MCP integrations under human review. He works in the same codebase as the platform team so phone, chat, and onboarding improvements reach production faster without bypassing quality gates.",
    responsibilities: [
      "Implement product features and UI improvements",
      "Maintain MCP servers and appointment booking flows",
      "Investigate bugs with reproducible fixes",
      "Write and update tests for critical paths",
      "Prepare PRs for human review before merge",
    ],
  },
  {
    id: "leo",
    name: "Leo",
    title: "AI Product Manager",
    avatarSrc: "/images/profiles/Leo.png",
    accentColor: "#9cbb5a",
    email: "product@siteglue.ai",
    description:
      "Leo keeps SiteGlue’s roadmap grounded in customer outcomes. He turns feedback from Trish, Victoria, and live deployments into clear specs, acceptance criteria, and release plans—so engineering and marketing stay aligned on what ships next.",
    responsibilities: [
      "Prioritize features from onboarding and support signals",
      "Write PRDs and user stories for AI Employee workflows",
      "Define acceptance criteria for HITL and handoff features",
      "Coordinate releases across phone, chat, and dashboard",
      "Track outcomes against business goals per industry",
    ],
  },
] as const satisfies readonly SiteGlueAiTeamMember[];

export type SiteGlueAiTeamMemberId = (typeof SITEGLUE_AI_TEAM)[number]["id"];
